import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { PageEmployees } from '../pages/pageEmployees';
import { PageClients } from '../pages/pageClients';
import { PageProjects } from '../pages/pageProjects';
import { PageReports } from '../pages/pageReports';
import { Page404 } from '../pages/page404';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import { PageActivity } from '../pages/pageActivity';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { PageProfile } from '../pages/pageProfile';
import { PageTasks } from '../pages/pageTasks';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import { PageHolidays } from '../pages/pageHolidays';
import { ProtectedRoute } from './protectedRoute';
import { RolesNamesBE } from '../models/employee';

export interface RouterElement {
  url: string;
  label?: string;
  icon?: OverridableComponent<SvgIconTypeMap<Record<string, never>, 'svg'>>;
  component: () => JSX.Element;
  allowedRoles?: string[];
}

export const ROUTER_ELEMENTS: RouterElement[] = [
  {
    url: '/profile',
    label: 'Profile',
    icon: PersonOutlineIcon,
    component: PageProfile,
    allowedRoles: [
      RolesNamesBE.ADMIN,
      RolesNamesBE.EMPLOYEE,
      RolesNamesBE.PROJECT_MANAGER,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.TEAM_LEAD,
    ],
  },
  {
    url: '/activity',
    label: 'Activity',
    icon: DashboardCustomizeOutlinedIcon,
    component: PageActivity,
    allowedRoles: [
      RolesNamesBE.ADMIN,
      RolesNamesBE.EMPLOYEE,
      RolesNamesBE.PROJECT_MANAGER,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.TEAM_LEAD,
    ],
  },
  {
    url: '/employees',
    label: 'Employees',
    icon: GroupOutlinedIcon,
    component: PageEmployees,
    allowedRoles: [RolesNamesBE.ADMIN, RolesNamesBE.TEAM_LEAD],
  },
  {
    url: '/projects',
    label: 'Projects',
    icon: DifferenceOutlinedIcon,
    component: PageProjects,
    allowedRoles: [
      RolesNamesBE.ADMIN,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.PROJECT_MANAGER,
    ],
  },

  {
    url: '/projects/client/:id',
    label: 'Projects',
    component: PageProjects,
    allowedRoles: [
      RolesNamesBE.ADMIN,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.PROJECT_MANAGER,
    ],
  },

  {
    url: '/projects/:id/tasks',
    label: 'Tasks',
    component: PageTasks,
    allowedRoles: [
      RolesNamesBE.ADMIN,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.PROJECT_MANAGER,
    ],
  },

  {
    url: '/clients',
    label: 'Clients',
    icon: GroupsOutlinedIcon,
    component: PageClients,
    allowedRoles: [
      RolesNamesBE.ADMIN,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.PROJECT_MANAGER,
    ],
  },
  {
    url: '/reports',
    label: 'Reports',
    icon: InventoryOutlinedIcon,
    component: PageReports,
    allowedRoles: [
      RolesNamesBE.ADMIN,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.PROJECT_MANAGER,
      RolesNamesBE.TEAM_LEAD,
    ],
  },
  {
    url: '/holidays',
    label: 'Holidays',
    icon: BeachAccessOutlinedIcon,
    component: PageHolidays,
    allowedRoles: [
      RolesNamesBE.ADMIN,
      RolesNamesBE.EMPLOYEE,
      RolesNamesBE.PROJECT_MANAGER,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.TEAM_LEAD,
    ],
  },
  {
    url: '*',
    component: Page404,
    allowedRoles: [
      RolesNamesBE.ADMIN,
      RolesNamesBE.EMPLOYEE,
      RolesNamesBE.PROJECT_MANAGER,
      RolesNamesBE.SALES_MANAGER,
      RolesNamesBE.TEAM_LEAD,
    ],
  },
];

export function RouterItems(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/activity" replace />} />
      {ROUTER_ELEMENTS.map((routerElement: RouterElement) => {
        return (
          <Route
            path={routerElement.url}
            element={
              <ProtectedRoute allowedRoles={routerElement.allowedRoles}>
                <routerElement.component />
              </ProtectedRoute>
            }
            key={`publicRoute_${routerElement.label}`}
          />
        );
      })}
    </Routes>
  );
}
